import { FC, ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { trackEvent } from "@/lib/analytics/tracking";

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Sends page_viewed ($pageview in PostHog, page_view in GA4 "Apex25") once per route change.
 * GA4 itself is loaded once in main.tsx (lib/analytics/ga4.ts); this used to inject a second,
 * hardcoded copy of the tag, which counted every page view twice.
 *
 * The route is the pathname with ids collapsed (/candidates/:id), so pages group in reports and
 * no id or query string is sent.
 */
const normalizeRoute = (pathname: string) =>
  pathname
    .split("/")
    .map((part) => (/\d/.test(part) && part.length >= 8 ? ":id" : part))
    .join("/")
    .replace(/(.)\/+$/, "$1");

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ({ children }) => {
  const { pathname } = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    // StrictMode runs effects twice in development: count each path once.
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    trackEvent("page_viewed", { page_route: normalizeRoute(pathname), page_title: document.title });
  }, [pathname]);

  return <>{children}</>;
};
